import { useCallback, useRef, useState } from "react"
import { Box, Typography } from "@mui/material"
import UploadIcon from "@mui/icons-material/CloudUploadOutlined"

/**
 * Drag-and-drop over an arbitrary area.
 *
 * ── The counter ────────────────────────────────────────────────────────────
 *
 * `dragenter` and `dragleave` fire for every child element the pointer crosses,
 * so a naive boolean flickers the overlay off the moment the cursor moves over
 * anything inside the zone. Counting enters minus leaves is the standard fix and
 * the reason this is a component rather than four lines inlined twice.
 *
 * @param {object} [sx] Merged onto the wrapper. A drop zone wrapping a scrolling
 *   region has to be a flex participant itself, or it sizes to its own content
 *   and the scroller inside it never gets a height to scroll within — which is
 *   exactly what had happened to the chat thread.
 */
export default function DropZone({ onFiles, disabled, children, label = "Drop files to attach", sx }) {
  const [over, setOver] = useState(false)
  const depth = useRef(0)

  const reset = useCallback(() => {
    depth.current = 0
    setOver(false)
  }, [])

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault()
      reset()
      if (disabled) return
      const files = [...(event.dataTransfer?.files || [])]
      if (files.length > 0) onFiles(files)
    },
    [disabled, onFiles, reset],
  )

  return (
    <Box
      onDragEnter={(event) => {
        event.preventDefault()
        if (disabled) return
        depth.current += 1
        setOver(true)
      }}
      onDragOver={(event) => {
        // Without preventDefault the browser navigates to the dropped file and
        // the drop handler never runs.
        event.preventDefault()
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        depth.current -= 1
        if (depth.current <= 0) reset()
      }}
      onDrop={handleDrop}
      sx={{ position: "relative", minWidth: 0, ...sx }}
    >
      {children}

      {over && !disabled ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 5,
            display: "grid",
            placeItems: "center",
            borderRadius: 2,
            border: 2,
            borderStyle: "dashed",
            borderColor: "primary.main",
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(126,211,72,0.14)" : "rgba(126,211,72,0.18)"),
            // The overlay must not eat the dragleave/drop events it sits on top of.
            pointerEvents: "none",
          }}
        >
          <Box sx={{ textAlign: "center", color: "primary.dark" }}>
            <UploadIcon sx={{ fontSize: 32 }} />
            <Typography variant="body2" fontWeight={650}>
              {label}
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
